import React, { useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, BookMarked, Link2, ChevronRight, Languages } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// Study Bible panel — opens a Dialog with AI-generated study notes for a verse.
export default function StudyPanel({ open, onOpenChange, verse, bookId, bookName, chapter, translation, onOpenRef }) {
  const { lang } = useApp();
  const [tab, setTab] = useState("verse"); // "verse" | "book"
  const [mode, setMode] = useState(() => localStorage.getItem("lumen_study_mode") || "standard");
  const [verseData, setVerseData] = useState(null);
  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wordOpen, setWordOpen] = useState(false);
  const [wordData, setWordData] = useState(null);
  const [wordLoading, setWordLoading] = useState(false);

  const changeMode = (m) => {
    setMode(m);
    localStorage.setItem("lumen_study_mode", m);
    setVerseData(null); // force refresh in new mode
  };

  const fetchVerse = async (opts = {}) => {
    if (!verse) return;
    const targetMode = opts.mode || mode;
    const showLoader = opts.silent !== true;
    if (showLoader) setLoading(true);
    try {
      const res = await api.post("/study/verse", {
        translation,
        book: bookId,
        book_name: bookName,
        chapter,
        verse: verse.verse,
        verse_text: verse.text,
        lang,
        mode: targetMode,
      });
      // Only update visible state if this response matches the currently-selected mode
      if (targetMode === mode) setVerseData(res.data);
    } catch (e) {
      if (showLoader) toast.error(lang === "es" ? "Error al generar notas" : "Failed to generate notes");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Silently pre-warm the other two modes so tapping their tabs is instant
  const prefetchOtherModes = () => {
    if (!verse) return;
    const others = ["standard", "pentecostal", "keyword"].filter((m) => m !== mode);
    // Fire-and-forget; backend returns cached copy if it already exists so this is idempotent
    others.forEach((m) => {
      fetchVerse({ mode: m, silent: true });
    });
  };

  const fetchBook = async () => {
    if (bookData) return;
    setLoading(true);
    try {
      const res = await api.post("/study/book-intro", {
        book: bookId,
        book_name: bookName,
        lang,
      });
      setBookData(res.data);
    } catch (e) {
      toast.error(lang === "es" ? "Error al cargar" : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const fetchWord = async (term, context) => {
    setWordOpen(true);
    setWordData(null);
    setWordLoading(true);
    try {
      const res = await api.post("/study/word", { term, context: context || "", lang });
      setWordData(res.data);
    } finally {
      setWordLoading(false);
    }
  };

  // Load on open + silently prefetch the other 2 modes so switching tabs feels instant
  React.useEffect(() => {
    if (open && tab === "verse" && !verseData) {
      fetchVerse();
      // Wait a moment so the visible fetch gets priority, then warm the other modes
      const id = setTimeout(prefetchOtherModes, 1200);
      return () => clearTimeout(id);
    }
    if (open && tab === "book" && !bookData) fetchBook();
    // eslint-disable-next-line
  }, [open, tab, mode]);

  // Reset when verse changes
  React.useEffect(() => {
    setVerseData(null);
  }, [verse?.verse, chapter, bookId, translation, lang]);

  const T = (en, es) => (lang === "es" ? es : en);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="study-panel"
          className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.3em] text-[10px]">
                {T("Study Bible", "Biblia de Estudio")}
              </span>
            </div>
            <DialogTitle className="font-serif text-2xl md:text-3xl tracking-tight">
              {bookName} {chapter}
              {tab === "verse" && verse ? `:${verse.verse}` : ""}
            </DialogTitle>
          </DialogHeader>

          {/* Study mode toggle */}
          <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
            <span className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mr-2">
              {T("Mode", "Modo")}
            </span>
            <button
              data-testid="study-mode-standard"
              onClick={() => changeMode("standard")}
              className={`px-3 h-7 rounded-full text-[11px] uppercase tracking-widest transition-colors duration-200 ${
                mode === "standard" ? "bg-[var(--text-primary)] text-[var(--bg)]" : "border border-[var(--border)] text-[var(--text-secondary)]"
              }`}
            >
              {T("Standard", "Estándar")}
            </button>
            <button
              data-testid="study-mode-pentecostal"
              onClick={() => changeMode("pentecostal")}
              className={`px-3 h-7 rounded-full text-[11px] uppercase tracking-widest transition-colors duration-200 ${
                mode === "pentecostal" ? "bg-[var(--accent)] text-white" : "border border-[var(--accent)]/40 text-[var(--accent)]"
              }`}
            >
              {T("Pentecostal", "Esencia")}
            </button>
            <button
              data-testid="study-mode-keyword"
              onClick={() => changeMode("keyword")}
              className={`px-3 h-7 rounded-full text-[11px] uppercase tracking-widest transition-colors duration-200 flex items-center gap-1 ${
                mode === "keyword" ? "bg-amber-700 text-white" : "border border-amber-700/40 text-amber-700"
              }`}
            >
              <Languages className="w-3 h-3" strokeWidth={1.75} />
              {T("Key Word", "Palabra Clave")}
            </button>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <button
              data-testid="study-tab-verse"
              onClick={() => setTab("verse")}
              className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest transition-colors duration-200 ${
                tab === "verse" ? "bg-[var(--text-primary)] text-[var(--bg)]" : "border border-[var(--border)] text-[var(--text-secondary)]"
              }`}
            >
              {T("Verse", "Versículo")}
            </button>
            <button
              data-testid="study-tab-book"
              onClick={() => setTab("book")}
              className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest transition-colors duration-200 ${
                tab === "book" ? "bg-[var(--text-primary)] text-[var(--bg)]" : "border border-[var(--border)] text-[var(--text-secondary)]"
              }`}
            >
              {T("Book intro", "Intro del libro")}
            </button>
          </div>

          {loading && (
            <div
              className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 flex flex-col items-center text-center"
              data-testid="study-loading"
            >
              <div className="w-8 h-8 rounded-full border-2 border-[var(--accent)] border-t-transparent animate-spin" />
              <p className="mt-4 font-serif text-lg text-[var(--text-primary)]">
                {mode === "keyword"
                  ? T("Consulting Hebrew & Greek sources…", "Consultando fuentes hebreas y griegas…")
                  : T("Preparing your study notes…", "Preparando tus notas de estudio…")}
              </p>
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                {mode === "keyword"
                  ? T("This can take ~15 seconds the first time on a new verse. Next time it's instant.", "Esto puede tardar ~15 s la primera vez en un versículo nuevo. La próxima vez es instantáneo.")
                  : T("About 5–10 seconds the first time on a new verse.", "Unos 5–10 s la primera vez en un versículo nuevo.")}
              </p>
              <div className="mt-4 space-y-2 w-full max-w-xs">
                <Skeleton className="h-2 w-3/4 mx-auto bg-[var(--surface-elevated)]" />
                <Skeleton className="h-2 w-full mx-auto bg-[var(--surface-elevated)]" />
                <Skeleton className="h-2 w-5/6 mx-auto bg-[var(--surface-elevated)]" />
              </div>
            </div>
          )}

          {!loading && tab === "verse" && verseData && (
            <div className="space-y-6" data-testid="study-verse-content">
              <blockquote className="border-l-2 border-[var(--accent)] pl-4 italic text-[var(--text-secondary)] font-serif-body">
                “{verse?.text}”
              </blockquote>

              <Section title={T("Context", "Contexto")}>
                <p className="text-[var(--text-primary)] leading-relaxed">{verseData.context}</p>
              </Section>
              <Section title={T("Meaning", "Significado")}>
                <p className="text-[var(--text-primary)] leading-relaxed">{verseData.meaning}</p>
              </Section>
              <Section title={T("Application", "Aplicación")}>
                <p className="text-[var(--text-primary)] leading-relaxed">{verseData.application}</p>
              </Section>

              {mode === "keyword" && verseData.hebrew_greek_words?.length > 0 && (
                <Section
                  title={bookId <= 39 ? T("Hebrew word study", "Estudio de palabras hebreas") : T("Greek word study", "Estudio de palabras griegas")}
                  Icon={Languages}
                >
                  <div className="space-y-3">
                    {verseData.hebrew_greek_words.map((w, i) => {
                      const isHebrew = bookId <= 39;
                      return (
                        <div
                          key={i}
                          data-testid={`hg-word-${i}`}
                          className="p-4 rounded-xl border border-amber-700/30 bg-amber-50/40 dark:bg-amber-950/20"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p
                                className={`font-serif text-2xl md:text-3xl tracking-tight text-amber-900 ${isHebrew ? "text-right" : ""}`}
                                dir={isHebrew ? "rtl" : "ltr"}
                                style={{ fontFamily: isHebrew ? "'SBL Hebrew','Ezra SIL','Times New Roman',serif" : "'SBL Greek','Cardo','Times New Roman',serif" }}
                              >
                                {w.original}
                              </p>
                              {w.transliteration && (
                                <p className="italic text-sm text-[var(--text-secondary)] mt-1">
                                  {w.transliteration}
                                </p>
                              )}
                            </div>
                            {w.strongs && (
                              <span className="shrink-0 uppercase tracking-widest text-[10px] px-2 py-1 rounded-full bg-amber-700 text-white font-mono">
                                {w.strongs}
                              </span>
                            )}
                          </div>
                          {w.root && (
                            <div className="mt-3">
                              <p className="uppercase tracking-[0.2em] text-[9px] text-[var(--text-secondary)]">
                                {T("Root", "Raíz")}
                              </p>
                              <p className="text-sm text-[var(--text-primary)] mt-0.5">{w.root}</p>
                            </div>
                          )}
                          {w.meaning && (
                            <div className="mt-3">
                              <p className="uppercase tracking-[0.2em] text-[9px] text-[var(--text-secondary)]">
                                {T("Semantic range", "Rango semántico")}
                              </p>
                              <p className="text-sm text-[var(--text-primary)] leading-relaxed mt-0.5">
                                {w.meaning}
                              </p>
                            </div>
                          )}
                          {w.usage_note && (
                            <div className="mt-3">
                              <p className="uppercase tracking-[0.2em] text-[9px] text-[var(--text-secondary)]">
                                {T("Use in this verse", "Uso en este versículo")}
                              </p>
                              <p className="text-sm text-[var(--text-primary)] leading-relaxed mt-0.5 italic">
                                {w.usage_note}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {verseData.key_words?.length > 0 && (
                <Section title={T("Key words", "Palabras clave")} Icon={BookMarked}>
                  <div className="space-y-2">
                    {verseData.key_words.map((w, i) => (
                      <button
                        key={i}
                        data-testid={`word-${i}`}
                        onClick={() => fetchWord(w.term, verse?.text)}
                        className="w-full text-left p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-serif text-base tracking-tight">{w.term}</span>
                          <ChevronRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">{w.explanation}</p>
                      </button>
                    ))}
                  </div>
                </Section>
              )}

              {verseData.cross_references?.length > 0 && (
                <Section title={T("Cross-references", "Referencias cruzadas")} Icon={Link2}>
                  <div className="space-y-2">
                    {verseData.cross_references.map((c, i) => (
                      <button
                        key={i}
                        data-testid={`xref-${i}`}
                        onClick={() => onOpenRef && onOpenRef(c.ref)}
                        className="w-full text-left p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
                            {c.ref}
                          </span>
                          <ChevronRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.5} />
                        </div>
                        <p className="text-sm mt-1 text-[var(--text-primary)]">{c.reason}</p>
                      </button>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}

          {!loading && tab === "book" && bookData && (
            <div className="space-y-5" data-testid="study-book-content">
              <Field label={T("Author", "Autor")} value={bookData.author} />
              <Field label={T("Date", "Fecha")} value={bookData.date} />
              <Field label={T("Audience", "Audiencia")} value={bookData.audience} />
              <Field label={T("Theme", "Tema")} value={bookData.theme} />
              {bookData.structure?.length > 0 && (
                <div>
                  <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-2">
                    {T("Structure", "Estructura")}
                  </p>
                  <ol className="space-y-1 list-decimal list-inside">
                    {bookData.structure.map((s, i) => (
                      <li key={i} className="text-[var(--text-primary)] leading-relaxed">{s}</li>
                    ))}
                  </ol>
                </div>
              )}
              {bookData.key_verse && (
                <button
                  data-testid="book-keyverse-btn"
                  onClick={() => onOpenRef && onOpenRef(bookData.key_verse)}
                  className="w-full text-left p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 group flex items-center justify-between"
                >
                  <div>
                    <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)]">
                      {T("Key verse", "Versículo clave")}
                    </p>
                    <p className="uppercase tracking-[0.25em] text-[11px] text-[var(--accent)] mt-1">
                      {bookData.key_verse}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.5} />
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Word study nested modal */}
      <Dialog open={wordOpen} onOpenChange={setWordOpen}>
        <DialogContent
          data-testid="word-panel"
          className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-md"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <BookMarked className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.3em] text-[10px]">
                {T("Word study", "Estudio de palabra")}
              </span>
            </div>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {wordData?.term || ""}
            </DialogTitle>
          </DialogHeader>
          {wordLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4 bg-[var(--surface)]" />
              <Skeleton className="h-4 w-full bg-[var(--surface)]" />
              <Skeleton className="h-4 w-5/6 bg-[var(--surface)]" />
            </div>
          ) : wordData ? (
            <div className="space-y-4">
              {wordData.original && (
                <Field label={T("Original", "Original")} value={wordData.original} />
              )}
              <Field label={T("Meaning", "Significado")} value={wordData.meaning} />
              {wordData.usage && <Field label={T("Usage", "Uso")} value={wordData.usage} />}
              {wordData.example_ref && (
                <button
                  onClick={() => { setWordOpen(false); onOpenRef && onOpenRef(wordData.example_ref); }}
                  className="w-full text-left p-3 rounded-lg border border-[var(--border)] hover:bg-[var(--surface)] transition-colors duration-200 flex items-center justify-between"
                >
                  <span className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
                    {wordData.example_ref}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.5} />
                </button>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />}
        {title}
      </p>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-1">{label}</p>
      <p className="text-[var(--text-primary)] leading-relaxed">{value}</p>
    </div>
  );
}
